'use client';

import { useTranslations } from '@fshr/context/i18n-context';
import { cn } from '@fshr/shared-react';
import { formatPrice } from '@micro-apps/shared';
import { flow, get, map, min } from 'lodash/fp';
import { DialogOfferDataType } from '../types';
import Information from '../icon/information';
import BevSafeText from '../bev-safe-text';

export const handlePriceFormat = ({
	amount = '',
	currencyCode = 'USD',
	locale = 'en',
}: {
	amount?: string;
	currencyCode?: string;
	locale?: string;
}): string => {
	try {
		const formattedPrice = formatPrice({
			currencyCode,
			value: amount,
			locale,
			decimalPlaces: 0,
		});

		if (typeof formattedPrice === 'string') {
			return formattedPrice;
		}

		return '';
	} catch (error) {
		console.log(error);
		return '';
	}
};

type PricingElementProps = {
	offer: DialogOfferDataType;
	mainContainerClassName?: string;
	allInPricing?: boolean;
	locale?: string;
	priceLabelContainerClassName?: string;
	offerPriceClassName?: string;
	offerEstimatedTotalClassName?: string;
	feesDisclaimerContainerClassName?: string;
	showNgmfInfoIcon?: boolean;
	onNgmfInfoClick?: () => void;
};

const hasSamePrices = offer => {
	const prices = map(get(['price', 'amount', 'withoutDecimal']))(offer.beds);
	return prices.every(val => val === prices[0]);
};

export function PricingComponent({
	offer,
	mainContainerClassName,
	locale = 'en',
	priceLabelContainerClassName,
	offerPriceClassName,
	feesDisclaimerContainerClassName = 'text-caption text-[#666666] font-bold w-full flex justify-start lg:justify-end',
	offerEstimatedTotalClassName,
	allInPricing,
	showNgmfInfoIcon,
	onNgmfInfoClick,
}: PricingElementProps) {
	const translate = useTranslations();
	const hasBedOptions = (offer?.beds?.length ?? 0) > 1;
	const lowestBedPrice = flow(map(get(['price', 'amount', 'withoutDecimal'])), min, price =>
		formatPrice({
			value: price,
			currencyCode: offer.price?.currency,
			decimalPlaces: 0,
			locale,
		})
	)(offer.beds);

	const getOfferPrice = () => {
		const parsedLowestPrice = typeof lowestBedPrice !== 'string' ? undefined : lowestBedPrice;
		const samePrice = hasSamePrices(offer);
		if (!parsedLowestPrice) {
			const offerPrice = handlePriceFormat({
				amount: offer.price?.amount?.withoutDecimal?.toString(),
				currencyCode: offer.price?.currency,
				locale,
			});

			return offerPrice;
		}
		return samePrice || !hasBedOptions
			? parsedLowestPrice
			: translate('From {0}', [parsedLowestPrice]);
	};

	return (
		<div className={mainContainerClassName}>
			<div
				className={cn(
					'text-caption flex gap-x-inline-xs items-start lg:items-center whitespace-nowrap lg:justify-end',
					priceLabelContainerClassName
				)}
			>
				<BevSafeText>{offer.price?.label}</BevSafeText>
				<span className={cn('text-subtitle2 font-bold', offerPriceClassName)}>
					{getOfferPrice()}
				</span>
				{showNgmfInfoIcon && onNgmfInfoClick ? (
					<button
						type="button"
						onClick={onNgmfInfoClick}
						aria-label={translate('View mandatory fee information')}
						className="ml-050 inline-flex items-center shrink-0"
					>
						<Information width={18} height={18} fill="#000" />
					</button>
				) : null}
			</div>
			{offer.feesDisclaimer ? (
				<div className={feesDisclaimerContainerClassName} data-cy="rate-card-fees-disclaimer">
					<span className="text-text-secondary">
						<BevSafeText>{offer.feesDisclaimer}</BevSafeText>
					</span>
				</div>
			) : allInPricing && offer.estimatedTotal ? (
				<div
					className={cn(
						'flex gap-x-inline-xs items-start lg:items-end lg:justify-end text-caption',
						priceLabelContainerClassName
					)}
				>
					<BevSafeText>{offer.estimatedTotal?.label}</BevSafeText>
					<span className={cn('text-subtitle2 font-bold', offerEstimatedTotalClassName)}>
						{handlePriceFormat({
							amount: offer.estimatedTotal?.amount?.withoutDecimal?.toString(),
							currencyCode: offer.estimatedTotal.currency,
						})}
					</span>
				</div>
			) : null}
		</div>
	);
}
